// Pure banner for the document overlay: what structured ingest knows about a
// tabular file — mapping state, its tables, whether the verbatim parquet is in
// the evidence store, and the projection it was executed as. Styled like the
// import-scope banner beside it (inline CSS vars, not Tailwind).

import { ExternalLink, FileSpreadsheet, Loader2, Table2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import type {
  MappingState,
  StructuredIngestAnalysis,
  StructuredIngestTable,
} from '@/services/api/structuredIngest'

export interface StructuredDataSectionProps {
  state: MappingState
  /** The full analysis once loaded; the banner still renders from `state` without it. */
  analysis: StructuredIngestAnalysis | null
  reviewHref: string
  onShowRows: (tableId: string, name: string) => void
}

const TITLE: Record<MappingState, string> = {
  preparing: 'Structured mapping is being prepared',
  review: 'Structured mapping ready for review',
  projected: 'Projected into the case graph',
  failed: 'Structured mapping failed',
}

const sub = {
  fontSize: 11.5,
  color: 'var(--muted-foreground)',
  fontFamily: 'var(--font-mono)',
  letterSpacing: 0.2,
  wordBreak: 'break-word',
} as const

function sum(values: Record<string, number> | undefined): number {
  return Object.values(values ?? {}).reduce((a, b) => a + b, 0)
}

function projectionLine(table: StructuredIngestTable): string | null {
  const p = table.projection
  if (!p) return null
  const windows = p.windows?.enabled ? `, ${p.windows.count} text windows` : ''
  return `projected ${p.run}: ${sum(p.nodes)} nodes, ${sum(p.edges)} edges${windows}`
}

export function StructuredDataSection({
  state,
  analysis,
  reviewHref,
  onShowRows,
}: StructuredDataSectionProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 14px',
        borderRadius: '12px',
        background: 'var(--primary-surface)',
        border: '1px solid var(--primary)',
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          flexShrink: 0,
          borderRadius: '8px',
          background: 'var(--accent)',
          border: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--primary)',
        }}
      >
        {state === 'preparing' ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <FileSpreadsheet size={14} />
        )}
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--primary)' }}>{TITLE[state]}</div>
        {state === 'failed' && analysis?.error && (
          <div style={{ ...sub, color: 'var(--destructive)' }}>{analysis.error}</div>
        )}
        {analysis?.tables.map(table => (
          <div key={table.tableId} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={sub}>
              {table.facts.name} · {table.facts.rows.toLocaleString()} rows ×{' '}
              {table.facts.columns.length} columns · {table.lane.lane.replace('_', ' ')}
              {table.parquetKey ? ' · verbatim parquet in the evidence store' : ''}
            </div>
            {projectionLine(table) && <div style={sub}>{projectionLine(table)}</div>}
            {table.proposal.plain_summary && (
              <div style={{ fontSize: 11.5, color: 'var(--muted-foreground)' }}>
                {table.proposal.plain_summary}
              </div>
            )}
            <button
              type="button"
              onClick={() => onShowRows(table.tableId, table.facts.name)}
              className="mt-0.5 p-0 max-md:py-1.5"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                alignSelf: 'flex-start',
                border: 'none',
                background: 'none',
                fontSize: 11.5,
                fontWeight: 500,
                color: 'var(--primary)',
                cursor: 'pointer',
              }}
            >
              <Table2 size={11} />
              Show rows
            </button>
          </div>
        ))}
        <Link
          to={reviewHref}
          className="max-md:py-1.5"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            marginTop: 4,
            fontSize: 11.5,
            fontWeight: 500,
            color: 'var(--primary)',
            textDecoration: 'none',
          }}
        >
          <ExternalLink size={11} />
          {state === 'projected' ? 'Open the mapping' : 'Review mapping'}
        </Link>
      </div>
    </div>
  )
}
